<template>
  <div class="cb-root sc-page-root">
    <FiPageRail title="Coaching Board" class="cb-rail">
      <template #actions>
        <!-- manual raise (the ops_desk "+ Add entry" affordance): most
             tickets arrive from scorecard flags or erm events, but a
             supervisor watching something those feeds can't see needs a
             way to put it in the queue directly -->
        <b-button size="sm" variant="primary" title="Raise a coaching or recognition ticket"
                  aria-label="Add ticket" @click="openAddTicket">
          <i class="fa fa-plus"></i><span class="cb-btn-label">&nbsp;Add ticket</span>
        </b-button>
        <coaching-portal-help />
      </template>
    </FiPageRail>

    <div v-if="actingTenantDiffers" class="cb-tenant-note">
      <i class="fa fa-building-o" aria-hidden="true"></i>
      Viewing <b>{{ actingTenantLabel }}</b> — not your selected fleet
    </div>

    <div v-if="loading && !tickets.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="reload()">Retry</b-button>
    </div>

    <div v-else :class="{ 'cb-stale': loading, 'cb-filters-collapsed': !filtersExpanded }">
      <!-- phone-only filters toggle, mirroring the Event Log and the ERM
           board: everything but the search pill folds away so a phone opens
           straight to the lanes. the badge counts narrowed dimensions,
           search excluded — it has its own always-visible field. -->
      <button
        type="button"
        class="cb-filter-toggle cb-mobile-only"
        :aria-expanded="filtersExpanded ? 'true' : 'false'"
        @click="filtersExpanded = !filtersExpanded"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="cb-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>

      <div class="cb-scope-row">
        <button type="button" :class="['cb-chip', { 'cb-chip--active': assigneeFilter === MINE }]"
                :aria-pressed="assigneeFilter === MINE ? 'true' : 'false'"
                @click="assigneeFilter = MINE">Mine ({{ myOpenCount }})</button>
        <button type="button" :class="['cb-chip', { 'cb-chip--active': assigneeFilter === '' }]"
                :aria-pressed="assigneeFilter === '' ? 'true' : 'false'"
                @click="assigneeFilter = ''">Everyone</button>
        <b-form-select v-model="assigneeDropdown" :options="assigneeOptions" size="sm"
                       class="cb-scope-select" aria-label="Filter by coach" />
        <!-- the signed-off window chips live in the Completed lane's header
             now (FI-2113): the control sits on the one lane it governs. -->
        <span class="cb-search">
          <i class="fa fa-search cb-search-icon" aria-hidden="true"></i>
          <input v-model="boardSearch" type="text" class="cb-search-input"
                 placeholder="Search driver, topic, or #12"
                 aria-label="Search board cards by driver, topic, note, or #id" />
          <button v-if="boardSearch" type="button" class="cb-search-clear" aria-label="Clear search"
                  @click="boardSearch = ''"><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
      </div>

      <div class="cb-board" ref="board">
        <div v-for="col in columnDefs" :key="col.status" class="cb-col" :class="'cb-col--' + col.status">
          <div class="cb-col-head">
            <span class="cb-status-chip" :class="'cb-status-chip--' + col.status">
              <i class="fa" :class="statusIcon(col.status)"></i>
              {{ statusLabel(col.status) }}
            </span>
            <span class="cb-col-count">{{ columns[col.status].length }}</span>
          </div>
          <!-- FI-2113 the closed lane's note leads with the window chips:
               the control IS the "last N days" part of the description -->
          <div v-if="col.closed" class="cb-col-note">
            <span v-if="!isMobileView">drag back to reopen</span>
            <!-- chips ride the right edge (the dashboard-widget header
                 arrangement), clear of the description text -->
            <span class="cb-lane-window" role="group" aria-label="Signed off within">
              <button v-for="d in closedWindows" :key="'cw-' + d" type="button"
                      :class="['cb-window-chip', { 'cb-window-chip--active': closedWindowDays === d }]"
                      :aria-pressed="closedWindowDays === d ? 'true' : 'false'"
                      @click="closedWindowDays = d">{{ d }}d</button>
            </span>
          </div>
          <div v-else class="cb-col-note">{{ col.note }}</div>
          <!-- closed lanes are pullable now (evan): a mis-click needs a way
               back. the drop is confirmed and audited rather than silent —
               see confirmReopen. -->
          <div class="cb-col-scroll"
               :class="laneShadowClasses('cb-col-scroll', col.status)"
               :ref="'laneScroll-' + col.status"
               @scroll.passive="onLaneScroll(col.status, $event)">
          <draggable :list="columns[col.status]" :sort="false"
                     :group="{ name: 'cb-board', pull: true, put: true }"
                     :disabled="saving || isMobileView"
                     ghost-class="cb-card--ghost" drag-class="cb-card--dragging"
                     class="cb-col-body" @change="onColumnChange(col.status, $event)"
                     @start="dragging = true" @end="dragging = false">
            <div v-for="t in columns[col.status]" :key="t.id"
                 class="cb-card"
                 :class="{
                   'cb-card--closed': col.closed,
                   'cb-card--overdue': t.past_due && !t.escalated,
                   'cb-card--escalated': t.escalated,
                   'cb-card--recognition': t.kind === 'recognition',
                 }"
                 @click="openTicket(t)">
              <div class="cb-card-top">
                <span class="cb-card-driver">{{ t.driver_name }}</span>
                <!-- the record's own header: which ticket, and when it was
                     raised. the full stamp is one hover away. -->
                <span class="cb-card-id" :title="'Created ' + formatDateTime(t.created_at)">
                  #{{ t.id }} · {{ formatCreated(t.created_at) }}
                </span>
              </div>
              <div class="cb-card-topic" :title="t.why || t.topic">
                {{ t.topic }}<span v-if="t.why" class="cb-card-why"> — {{ t.why }}</span>
              </div>
              <div class="cb-card-foot">
                <span class="cb-kind-chip" :class="'cb-kind-chip--' + (t.kind || 'coaching')">
                  <i class="fa" :class="t.kind === 'recognition' ? 'fa-star-o' : 'fa-check-square-o'"></i>
                </span>
                <span v-if="t.acknowledged_in_person" class="cb-flag cb-flag--ack"
                      title="The driver acknowledged this conversation in person">
                  <i class="fa fa-handshake-o"></i>
                </span>
                <span v-if="t.escalated" class="cb-flag cb-flag--escalated">Escalated</span>
                <span v-else-if="t.past_due" class="cb-flag cb-flag--overdue">{{ t.days_overdue }}d overdue</span>
                <span v-else-if="col.closed && t.sign_off" class="cb-flag cb-flag--signed" :title="'Signed by ' + t.sign_off.by_name">
                  {{ formatDate(t.sign_off.at) }}
                </span>
                <span v-else-if="t.status === 'scheduled' && t.scheduled_for" class="cb-flag">{{ formatDate(t.scheduled_for) }}</span>
                <span v-else-if="t.due_date" class="cb-flag">due {{ formatDate(t.due_date) }}</span>
                <span class="ml-auto">
                  <span v-if="t.assigned_to === user.username" class="cb-you-chip">You</span>
                  <span v-else class="cb-assignee">{{ firstName(t.assigned_to_name || t.assigned_to) }}</span>
                </span>
              </div>
            </div>
          </draggable>

          <!-- FI-2113 a lane with nothing to show says so, instead of
               rendering a bare drop slot. hidden while a card is in flight
               (the ghost needs the space), and worded as "no matches" when
               it is the filters that emptied the lane. -->
          <div v-if="!columns[col.status].length && !loading && !dragging" class="cb-col-empty">
            {{ laneEmptyText(col) }}
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- manual "+ Add ticket" create, shared with Coaching Tickets -->
    <coaching-add-ticket-modal ref="addTicket" :tenant="tenant"
                               :tickets="tickets" :settings="settings"
                               @created="loadTickets" />

    <!-- reopening a signed record: say plainly what it does, because the
         signature goes away and the undo lands on the permanent trail -->
    <b-modal id="cb-reopen-modal" hide-footer title="Reopen this coaching record?"
             @hidden="onReopenHidden">
      <div v-if="reopenTicket">
        <p class="cb-modal-lead">
          <b>#{{ reopenTicket.id }} — {{ reopenTicket.driver_name }}</b>, {{ reopenTicket.topic }}
        </p>
        <p class="cb-small">
          This was signed off
          <span v-if="reopenTicket.sign_off">
            by <b>{{ reopenTicket.sign_off.by_name }}</b> on {{ formatDate(reopenTicket.sign_off.at) }}</span>.
          Reopening clears that signature so the ticket can be worked and signed again.
          Nothing is erased: the original comment stays on the trail, and this reopen
          is recorded with your name.
        </p>

        <div class="cb-label">Reopen as</div>
        <b-form-select v-model="reopenToStatus" size="sm" class="mb-3 cb-reopen-select"
                       :options="[{ value: 'open', text: 'Open' }, { value: 'scheduled', text: 'Scheduled' }]" />

        <div class="cb-label">Reason <span class="cb-hint">optional — it goes on the record</span></div>
        <b-form-textarea v-model="reopenReason" rows="2" class="mb-3"
                         placeholder="e.g. signed off on the wrong driver" />

        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('cb-reopen-modal')">Cancel</b-button>
          <b-button variant="warning" :disabled="reopenSaving" @click="saveReopen">
            {{ reopenSaving ? 'Reopening…' : 'Reopen ticket' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <coaching-signoff-modal ref="signoff" :tenant="tenant"
                            @changed="reload" @hidden="onSignoffHidden" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import draggable from 'vuedraggable'
import {
  get_coaching_tickets, update_coaching_ticket, reopen_coaching_ticket,
} from '../networking/apis'
import { resolveActingTenant, tenantDisplayName } from '../utils/tenant_context'
import swimLaneViewport from '../utils/swim_lane_viewport'
import { OPEN_STATUSES, statusLabel, statusIcon } from '../utils/coaching_labels'
import { formatDateTimeCompactYear } from '../helpers/dates'
import CoachingPortalHelp from './coaching_portal_help.vue'
import CoachingSignoffModal from './coaching_signoff_modal.vue'
import CoachingAddTicketModal from './coaching_add_ticket_modal.vue'

// drag is off at or below this width. matches the @media (max-width: 768px)
// block at the foot of this file — the two must agree, or the card would
// still say "grab" while refusing to be dragged.
const MOBILE_MAX_WIDTH = 768

// the signed-off lanes' default trailing window, in days. named so the
// mobile badge can tell "left at the default" from "narrowed".
const DEFAULT_CLOSED_WINDOW = 7

export default {
  name: 'coaching_board',
  components: { draggable, CoachingPortalHelp, CoachingSignoffModal, CoachingAddTicketModal },
  mixins: [swimLaneViewport],
  data() {
    return {
      loading: false,
      error: null,
      saving: false,
      tickets: [],

      // the swim lanes. draggable mutates these arrays on drop, so they are
      // plain data rebuilt from `tickets` — never computed.
      columns: { open: [], scheduled: [], completed: [] },
      // FI-2113 a card is in flight; the empty-lane hints step aside
      dragging: false,

      // a drop onto a closed lane is provisional until the sign-off saves:
      // the card has already moved visually, so if the modal closes without
      // a write we rebuild the lanes to snap it back
      pendingSignoffDrop: false,

      // same scope control as Coaching Tickets: '' = everyone, MINE = the
      // signed-in user, any other string = that coach's username
      assigneeFilter: '',
      MINE: '__mine__',
      scopeInitialized: false,

      boardSearch: '',

      // closed lanes accumulate forever, so they show a trailing window
      // while Open/Scheduled always show everything outstanding — a date
      // filter that could hide live work would defeat the board.
      closedWindowDays: DEFAULT_CLOSED_WINDOW,
      closedWindows: [7, 30, 90],

      // ≤600px folds the filter controls behind a toggle so a phone opens on
      // the lanes rather than on a wall of chips (Event Log precedent).
      filtersExpanded: false,

      // ≤768px turns dragging OFF entirely. sortablejs starts a drag as soon
      // as a touch moves, which on a phone is indistinguishable from
      // scrolling the lane, so cards got picked up on every scroll. Touch
      // widths change status the way Coaching Tickets does instead: tap the
      // card, act on the ticket. Kept in sync by a resize listener wired in
      // mounted(). Mirrors the ERM board.
      isMobileView: false,

      // reopen (drag out of a closed lane) — provisional until it saves
      reopenTicket: null,
      reopenToStatus: 'open',
      reopenReason: '',
      reopenSaving: false,
      pendingReopenDrop: false,

      // tenant SLA settings ride back on the list response (read-only
      // here) — passed to the add-ticket modal for its due-date prefill
      settings: null,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    tenant() {
      return resolveActingTenant(this.user, this.$route.query.tenant)
    },
    actingTenantDiffers() {
      return String(this.tenant) !== String(this.user.tenant)
    },
    actingTenantLabel() {
      return tenantDisplayName(this.user, this.tenant)
    },
    columnDefs() {
      return [
        { status: 'open', note: 'raised, not yet handled', empty: 'Nothing raised' },
        { status: 'scheduled', note: 'the conversation is booked in', empty: 'No conversations booked in' },
        { status: 'completed', closed: true },
      ]
    },
    // badge on the mobile toggle: how many dimensions are narrowed while the
    // panel is folded away. everything counts, search included — it folds
    // away with the rest now, so nothing else on screen would show that a
    // search is trimming the lanes.
    activeFilterCount() {
      let n = 0
      if (this.boardSearch && this.boardSearch.trim()) n++
      if (this.assigneeFilter) n++
      // the signed-off window is NOT counted: it lives on the lane header
      // now, visible even while the filter panel is folded away
      return n
    },
    myOpenCount() {
      return this.tickets.filter(t => t.kind !== 'recognition'
        && t.assigned_to === this.user.username
        && OPEN_STATUSES.includes(t.status)).length
    },
    scopedTickets() {
      let rows = this.tickets
      if (this.assigneeFilter) {
        const target = this.assigneeFilter === this.MINE ? this.user.username : this.assigneeFilter
        rows = rows.filter(t => t.assigned_to === target)
      }
      const q = this.boardSearch.trim().toLowerCase()
      if (q) {
        // "#12" (or a bare number) searches the ticket id exactly, matching
        // the Coaching Tickets search
        const idQuery = /^#?\d+$/.test(q) ? Number(q.replace('#', '')) : null
        rows = rows.filter(t => {
          if (idQuery !== null) return t.id === idQuery
          return [t.driver_name, t.topic, t.why, t.assigned_to_name, t.assigned_to]
            .filter(Boolean).join(' ').toLowerCase().includes(q)
        })
      }
      return rows
    },
    assigneeOptions() {
      const by = {}
      this.tickets.forEach(t => {
        if (!t.assigned_to) return
        const row = by[t.assigned_to] = by[t.assigned_to] || {
          name: t.assigned_to_name || t.assigned_to, open: 0,
        }
        if (OPEN_STATUSES.includes(t.status)) row.open += 1
      })
      const coaches = Object.entries(by)
        .sort((a, b) => a[1].name.localeCompare(b[1].name))
        .map(([username, row]) => ({
          value: username,
          text: `${row.name}${row.open ? ` (${row.open} open)` : ''}`,
        }))
      return [{ value: null, text: 'By coach…' }, ...coaches]
    },
    assigneeDropdown: {
      get() {
        return (this.assigneeFilter === '' || this.assigneeFilter === this.MINE)
          ? null : this.assigneeFilter
      },
      set(value) {
        this.assigneeFilter = value || ''
      },
    },
  },
  watch: {
    // the navbar tenant switcher mutates user.tenant in place without
    // remounting the page (ops_desk.vue precedent). per-tenant ticket ids
    // collide and a sign-off is permanent — drop every trace of the old
    // tenant's queue before it can be acted on.
    tenant() {
      this.$refs.signoff.resetTenantScope()
      this.$refs.addTicket.resetTenantScope()
      this.pendingSignoffDrop = false
      this.tickets = []
      this.columns = { open: [], scheduled: [], completed: [] }
      this.assigneeFilter = ''
      this.scopeInitialized = false
      this.boardSearch = ''
      this.closedWindowDays = DEFAULT_CLOSED_WINDOW
      this.settings = null
      this.loadTickets()
    },
    assigneeFilter() {
      this.rebuildColumns()
    },
    boardSearch() {
      this.rebuildColumns()
    },
    closedWindowDays() {
      this.rebuildColumns()
    },
  },
  created() {
    this.loadTickets()
  },
  mounted() {
    this._onResize = () => { this.isMobileView = window.innerWidth <= MOBILE_MAX_WIDTH }
    this._onResize()
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    statusLabel, statusIcon,
    formatDate(d) { return d ? moment(d.slice(0, 10)).format('MMM D') : '—' },
    formatDateTime(d) { return d ? moment(d).format('ddd, MMM D, YYYY · h:mm A') : '—' },
    formatCreated(d) { return formatDateTimeCompactYear(d) || '—' },
    firstName(name) { return (name || '').split(' ')[0] },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },

    async loadTickets() {
      this.loading = true
      this.error = null
      try {
        const resp = await get_coaching_tickets(this.tenant)
        const payload = (resp.value && resp.value.data) || {}
        this.tickets = payload.tickets || []
        if (payload.settings) this.settings = payload.settings
        if (!this.scopeInitialized) {
          this.assigneeFilter = this.myOpenCount > 0 ? this.MINE : ''
          this.scopeInitialized = true
        }
        this.rebuildColumns()
      } catch (e) {
        this.error = this.apiErrorMessage(e, 'Failed to load the coaching queue.')
      } finally {
        this.loading = false
      }
    },
    reload() {
      return this.loadTickets()
    },
    // the definite claim ("Nothing raised") is only honest when the data is
    // actually empty; when the scope or search emptied the lane, say that
    laneEmptyText(col) {
      if (this.assigneeFilter || (this.boardSearch && this.boardSearch.trim())) {
        return 'Nothing here matches the filters'
      }
      return col.closed ? `Nothing signed off in the last ${this.closedWindowDays} days` : col.empty
    },

    // when a ticket was closed. completed_at is the sign-off stamp; the
    // fallbacks cover rows written before it existed, and a row with no
    // usable date is shown rather than hidden — a missing timestamp must
    // never make a coaching record disappear.
    closedOnOrAfter(ticket, cutoff) {
      const raw = ticket.completed_at || ticket.updated_at || ticket.created_at
      if (!raw) return true
      const at = moment(raw)
      return at.isValid() ? at.isSameOrAfter(cutoff) : true
    },
    rebuildColumns() {
      const cols = { open: [], scheduled: [], completed: [] }
      const cutoff = moment().subtract(this.closedWindowDays, 'days').startOf('day')
      this.scopedTickets.forEach(t => {
        if (!cols[t.status]) return
        // open + scheduled are never date-filtered: outstanding work stays
        // visible however old it is
        if (!OPEN_STATUSES.includes(t.status) && !this.closedOnOrAfter(t, cutoff)) return
        cols[t.status].push(t)
      })
      // open: the fires first (escalated, then overdue, then nearest due
      // date). scheduled: soonest conversation first. closed: newest first.
      const dueRank = t => (t.escalated ? 0 : t.past_due ? 1 : 2)
      cols.open.sort((a, b) => dueRank(a) - dueRank(b)
        || String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')))
      cols.scheduled.sort((a, b) =>
        String(a.scheduled_for || a.due_date || '9999').localeCompare(String(b.scheduled_for || b.due_date || '9999')))
      const closedSort = (a, b) => String(b.completed_at || '').localeCompare(String(a.completed_at || ''))
      cols.completed.sort(closedSort)
      this.columns = cols
    },

    onColumnChange(toStatus, evt) {
      if (!evt.added) return
      const ticket = evt.added.element
      // dragged out of Completed: this un-signs a personnel
      // record, so it asks first and the card only stays put if it saves
      if (ticket.sign_off || !OPEN_STATUSES.includes(ticket.status)) {
        this.reopenTicket = ticket
        this.reopenToStatus = OPEN_STATUSES.includes(toStatus) ? toStatus : 'open'
        this.reopenReason = ''
        this.pendingReopenDrop = true
        this.$bvModal.show('cb-reopen-modal')
        return
      }
      if (OPEN_STATUSES.includes(toStatus)) {
        this.moveOpenTicket(ticket, toStatus)
      } else {
        // the card sits in the closed lane provisionally; the sign-off is
        // what actually closes it. cancelling snaps it back.
        this.pendingSignoffDrop = true
        this.$refs.signoff.open(ticket)
      }
    },
    async moveOpenTicket(ticket, toStatus) {
      this.saving = true
      try {
        const payload = toStatus === 'scheduled'
          ? { status: 'scheduled', scheduled_for: moment().add(7, 'days').format('YYYY-MM-DD') }
          : { status: 'open', scheduled_for: null }
        await update_coaching_ticket(this.tenant, ticket.id, payload)
        this.$bvToast.toast(
          toStatus === 'scheduled'
            ? `Ticket #${ticket.id} marked scheduled.`
            : `Ticket #${ticket.id} moved back to open.`,
          { title: 'Coaching Portal', variant: 'info', autoHideDelay: 3000 },
        )
        await this.loadTickets()
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not update the ticket.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
        // the drag already moved the card — put it back where the data says
        this.rebuildColumns()
      } finally {
        this.saving = false
      }
    },
    async saveReopen() {
      this.reopenSaving = true
      try {
        await reopen_coaching_ticket(this.tenant, this.reopenTicket.id, {
          status: this.reopenToStatus,
          reason: this.reopenReason.trim() || undefined,
        })
        // saved: the card keeps its new lane, so the drop is no longer
        // provisional and onReopenHidden must not snap it back
        this.pendingReopenDrop = false
        this.$bvModal.hide('cb-reopen-modal')
        this.$bvToast.toast(
          `Ticket #${this.reopenTicket.id} reopened — the signature was cleared and the change is on the trail.`,
          { title: 'Coaching Portal', variant: 'warning', autoHideDelay: 5000 },
        )
        await this.loadTickets()
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not reopen the ticket.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
      } finally {
        this.reopenSaving = false
      }
    },
    onReopenHidden() {
      // cancelled: the drag already moved the card, so put it back
      if (this.pendingReopenDrop) this.rebuildColumns()
      this.pendingReopenDrop = false
      this.reopenTicket = null
    },
    onSignoffHidden({ mutated }) {
      if (this.pendingSignoffDrop && !mutated) this.rebuildColumns()
      this.pendingSignoffDrop = false
    },
    openTicket(ticket) {
      this.pendingSignoffDrop = false
      this.$refs.signoff.open(ticket)
    },
    openAddTicket() {
      this.$refs.addTicket.open()
    },
  },
}
</script>

<style scoped>
/* the app shell centers text (#app) — this page is left-aligned, so inline
   controls (the mobile Filters toggle) sit left like everything else */
/* FI-2113: the board fills the viewport, so page-bottom breathing room
   would just force a page scrollbar; under horizon the root also bleeds
   into the pane's 20px bottom gutter, leaving the rail's 1px sliver */
.cb-root { padding-bottom: 2px; text-align: left; }
.fi-hz-content .cb-root { margin-bottom: -19px; }
.cb-stale { opacity: 0.6; }
.cb-tenant-note { margin-top: 10px; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }
.cb-scope-row { display: flex; align-items: center; gap: 6px; margin: 14px 0 12px; }
.cb-scope-select { max-width: 220px; font-size: 0.85em; }
/* FI-2113 the signed-off window, on the lane it governs — right edge of
   the note row, where it replaces the "last N days" words */
.cb-lane-window { display: inline-flex; gap: 3px; flex-shrink: 0; margin-left: auto; }
.cb-window-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  padding: 1px 7px; font-size: 10.5px; font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.cb-window-chip:hover:not(.cb-window-chip--active) { background: var(--fi-surface-sunken, #eef4fb); }
.cb-window-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}

/* search pill anchored to the right of the scope row (od-search recipe) */
.cb-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 240px; min-width: 170px;
}
.cb-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.cb-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #1d1d1f);
  transition: border-color .12s, box-shadow .12s;
}
.cb-search-input::placeholder { color: #9aa3ad; }
.cb-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.cb-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
.cb-search-clear:hover { color: #4b5563; }
.cb-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* the active chip keeps its own fill on hover (see erm_board) */
.cb-chip:hover:not(.cb-chip--active) { background: var(--fi-surface-sunken, #eef4fb); }
.cb-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}

/* the board: four equal lanes, horizontal scroll when squeezed.
   align-items:stretch (not flex-start) so every lane is the same height —
   with lanes sized to their contents, a near-empty column's drop zone was
   only as tall as its 60px body, and dragging a card across from a long
   Open column missed it entirely (evan: "could not get an open ticket to
   this status"). */
/* FI-2113 the board is a viewport-bound surface: it fills the space under
   the chrome above it (measured into --sl-board-top by the shared mixin)
   and each lane scrolls itself, so lane headers and every drop target stay
   on screen for the whole drag and the page itself never scrolls. */
.cb-board {
  display: flex; gap: 12px; align-items: stretch; overflow-x: auto;
  /* the 6px gap constant is mirrored in swim_lane_viewport.js's clamp
     detection -- change them together */
  /* vh, NOT dvh: the build's cssnano parses dvh fine on its own (see
     fleet_assistant_launcher) but NOT inside calc() -- a dvh calc fails
     the whole css minification step and breaks the pipeline. mobile
     browser chrome is covered by the mixin's measured shave instead. */
  height: calc(100vh - var(--sl-board-top, 320px) - 6px - var(--sl-board-shave, 0px));
  min-height: 320px;
  padding-bottom: 2px;
}
.cb-col {
  flex: 1 1 0; min-width: 230px; min-height: 0;
  display: flex; flex-direction: column;
  background: var(--fi-surface-sunken, #f4f6f8);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 8px;
  padding: 8px;
  /* fallback drag selects text across lanes otherwise (see erm_board) */
  user-select: none; -webkit-user-select: none;
}
/* the lane's own scroll area; head + note above it never scroll. sticky
   zero-height pseudos paint the scroll shadows (see erm_board). */
.cb-col-scroll {
  position: relative; flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  overflow-y: auto;
  margin: 0 -4px; padding: 0 4px;
}
.cb-col-scroll::before,
.cb-col-scroll::after {
  content: ''; display: block; flex: 0 0 0px; height: 0;
  position: sticky; z-index: 2; pointer-events: none;
  opacity: 0; transition: opacity .15s;
}
.cb-col-scroll::before { top: 0; order: -1; box-shadow: 0 7px 9px -6px rgba(15, 23, 42, 0.35); }
.cb-col-scroll::after { bottom: 0; box-shadow: 0 -7px 9px -6px rgba(15, 23, 42, 0.35); }
.cb-col-scroll--shadow-top::before { opacity: 1; }
.cb-col-scroll--shadow-bottom::after { opacity: 1; }
/* midnight's lanes are dark, so a navy shadow vanishes — cast deeper black */
[data-fi-theme="midnight"] .cb-col-scroll::before { box-shadow: 0 7px 10px -6px rgba(0, 0, 0, 0.8); }
[data-fi-theme="midnight"] .cb-col-scroll::after { box-shadow: 0 -7px 10px -6px rgba(0, 0, 0, 0.8); }
/* FI-2113 empty lane: say so, centered in the (still droppable) space */
.cb-col-empty {
  position: absolute; left: 6px; right: 6px; top: 6px; bottom: 6px;
  display: flex; align-items: center; justify-content: center;
  text-align: center; padding: 8px;
  font-size: 12px; color: var(--fi-text-muted, #8a94a1);
  border: 1px dashed var(--fi-line-strong, #d6dde6); border-radius: 6px;
  pointer-events: none;
}
.cb-col-head { display: flex; align-items: center; justify-content: space-between; padding: 2px 2px 0; }
.cb-col-count { font-size: 12px; font-weight: 700; color: var(--fi-text-muted, #6b7280); font-variant-numeric: tabular-nums; }
.cb-col-note {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 2px 2px 6px;
}
/* the lane body fills the scroll area so the whole visible lane is a drop
   target, empty or not */
.cb-col-body {
  flex: 1 0 auto; min-height: 60px;
  display: flex; flex-direction: column; gap: 6px;
}
/* lane top accents follow the status chip families */
.cb-col--open { box-shadow: inset 0 3px 0 0 #9aa4b1; }
.cb-col--scheduled { box-shadow: inset 0 3px 0 0 #f0d97a; }
.cb-col--completed { box-shadow: inset 0 3px 0 0 #7bcf99; }

.cb-card {
  /* token, not #fff: midnight (FI-1869) re-resolves --fi-surface dark, so
     the card keeps its contrast against the sunken lane in both themes */
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #dfe4ea);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: grab;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
}
.cb-card:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.cb-card--closed { cursor: grab; opacity: 0.82; }
.cb-modal-lead { margin-bottom: 6px; }
.cb-small { font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }
.cb-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cb-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cb-reopen-select { max-width: 220px; }
/* flagged cards carry the house rail edge (fi-pr-rail / ob-rail): square
   3px accent, soft right side -- not a radius-clipped inset stripe. the
   :hover guards keep the accent from being repainted by .cb-card:hover. */
.cb-card--overdue,
.cb-card--escalated { border-radius: 0 6px 6px 0; }
.cb-card--overdue { border-left: 3px solid #e8912d; }
.cb-card--escalated { border-left: 3px solid #d62f2f; }
.cb-card--overdue:hover { border-left-color: #e8912d; }
.cb-card--escalated:hover { border-left-color: #d62f2f; }
.cb-card--ghost { opacity: 0.4; border-style: dashed; }
.cb-card--dragging { cursor: grabbing; }
.cb-card-top { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
.cb-card-driver { font-weight: 700; }
.cb-card-id { font-size: 11px; color: var(--fi-text-muted, #8a94a1); font-variant-numeric: tabular-nums; }
.cb-card-topic {
  margin-top: 2px; color: var(--fi-text, #374151);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.cb-card-why { color: var(--fi-text-muted, #6b7280); }
.cb-card-foot { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
.cb-flag { font-size: 11px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.cb-flag--overdue { color: #b45309; font-weight: 600; }
.cb-flag--escalated { color: #b91c1c; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
.cb-flag--signed { color: #166534; }
.cb-flag--ack { color: #0b3d91; }
.cb-assignee { font-size: 11px; color: var(--fi-text-muted, #6b7280); }
.cb-you-chip {
  display: inline-block; padding: 1px 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
}
/* chip families shared with the queue table (cp- copies are scoped there) */
.cb-status-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cb-status-chip .fa { font-size: 10px; line-height: 1; }
.cb-status-chip--open         { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.cb-status-chip--scheduled    { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.cb-status-chip--completed    { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }
.cb-kind-chip {
  display: inline-flex; align-items: center;
  padding: 1px 6px;
  font-size: 10px; line-height: 1.4;
  border-radius: 999px; border: 1px solid;
}
.cb-kind-chip--coaching    { background: #fee4d1; color: #b85a1e; border-color: #f0c7a4; }
.cb-kind-chip--recognition { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }

/* mobile: the header stays one row — title and actions inline. overrides
   FiPageRail's own 600px rule that wraps actions onto a full-width second
   row (fine for pages with a toolbar; a waste for one info icon). */
@media (max-width: 768px) {
  /* dragging is disabled at this width (see isMobileView), so the card must
     not advertise a grab it will not honour — it is a tap target. */
  .cb-card { cursor: pointer; }
}

.cb-mobile-only { display: none; }
.cb-filter-toggle {
  align-items: center; gap: 7px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 7px 14px; margin: 14px 0 10px;
  font-size: 13.5px; font-weight: 600;
  color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.cb-filter-toggle:hover { background: var(--fi-surface-sunken, #eef4fb); }
.cb-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.cb-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}

@media (max-width: 600px) {
  .cb-rail.fi-pr-rail { flex-wrap: nowrap; row-gap: 0; }
  .cb-rail >>> .fi-pr-actions { width: auto; }
  /* the add button drops to icon-only beside the title (od-btn-label rule) */
  .cb-btn-label { display: none; }

  /* mobile-only affordances turn on here */
  .cb-mobile-only { display: inline-flex; }
  /* collapsed by default: every control folds away, search included, so a
     phone opens straight onto the lanes and spends no rows on chrome */
  .cb-filters-collapsed .cb-scope-row { display: none; }
  .cb-scope-row { flex-wrap: wrap; }
  .cb-search { width: 100%; }

  /* one lane per screen instead of a ~950px scrubbable strip. the lane takes
     86% so the next one's edge stays visible — that sliver is the only thing
     telling you there are more — and the scroll snaps so a swipe lands square
     on a lane rather than halfway between two. only on phones: a tablet has
     room for the overview and should keep it. */
  .cb-board {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .cb-col {
    flex: 0 0 86%;
    min-width: 0;
    scroll-snap-align: start;
  }
}
</style>
